import { useCallback, useEffect, useState } from 'react';
import userService, {
  ReferentFunctionDefinition,
  ReferentTypeDefinition,
} from '../../../../shared/services/userService';

/** Hook extrait de ReferentDefinitionsContent.tsx : CRUD types + fonctions référent. */

export function useReferentDefinitions() {
  const [types, setTypes] = useState<ReferentTypeDefinition[]>([]);
  const [definitions, setDefinitions] = useState<ReferentFunctionDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /* Nouveau type */
  const [newTypeKey, setNewTypeKey] = useState('');
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeDesc, setNewTypeDesc] = useState('');

  /* Nouvelle fonction */
  const [newKey, setNewKey] = useState('');
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newFunctionTypeId, setNewFunctionTypeId] = useState<number | ''>('');

  const [editingType, setEditingType] = useState<ReferentTypeDefinition | null>(null);
  const [editTypeKey, setEditTypeKey] = useState('');
  const [editTypeName, setEditTypeName] = useState('');
  const [editTypeDesc, setEditTypeDesc] = useState('');
  const [editTypeActive, setEditTypeActive] = useState(true);

  const [editing, setEditing] = useState<ReferentFunctionDefinition | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editFunctionTypeId, setEditFunctionTypeId] = useState<number | ''>('');
  const [editActive, setEditActive] = useState(true);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const [resTypes, resDefs] = await Promise.all([
        userService.getReferentTypes(),
        userService.getReferentDefinitions(),
      ]);
      if (resTypes.success) setTypes(resTypes.data);
      if (resDefs.success) setDefinitions(resDefs.data);
    } catch (e: any) {
      setError(e.message || 'Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateType = async () => {
    setSaving(true);
    setError(null);
    try {
      await userService.createReferentType({
        type_key: newTypeKey.trim(),
        name: newTypeName.trim(),
        description: newTypeDesc.trim() || undefined,
      });
      setNewTypeKey('');
      setNewTypeName('');
      setNewTypeDesc('');
      await load();
    } catch (e: any) {
      setError(e.message || 'Erreur à la création du type');
    } finally {
      setSaving(false);
    }
  };

  const openEditType = (t: ReferentTypeDefinition) => {
    setEditingType(t);
    setEditTypeKey(t.type_key);
    setEditTypeName(t.name);
    setEditTypeDesc(t.description || '');
    setEditTypeActive(!!t.is_active);
  };

  const handleSaveEditType = async () => {
    if (!editingType) return;
    setSaving(true);
    setError(null);
    try {
      await userService.updateReferentType(editingType.id, {
        type_key: editTypeKey.trim(),
        name: editTypeName.trim(),
        description: editTypeDesc.trim() || undefined,
        is_active: editTypeActive,
      });
      setEditingType(null);
      await load();
    } catch (e: any) {
      setError(e.message || 'Erreur à la mise à jour du type');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivateType = async (t: ReferentTypeDefinition) => {
    if (
      !window.confirm(
        `Désactiver le type « ${t.name} » ? Impossible s'il est encore utilisé par des fonctions actives.`
      )
    ) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await userService.deactivateReferentType(t.id);
      await load();
    } catch (e: any) {
      setError(e.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (newFunctionTypeId === '') return;
    setSaving(true);
    setError(null);
    try {
      await userService.createReferentDefinition({
        function_key: newKey.trim(),
        name: newName.trim(),
        description: newDesc.trim() || undefined,
        referent_type_id: newFunctionTypeId,
      });
      setNewKey('');
      setNewName('');
      setNewDesc('');
      setNewFunctionTypeId('');
      await load();
    } catch (e: any) {
      setError(e.message || 'Erreur à la création');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (d: ReferentFunctionDefinition) => {
    setEditing(d);
    setEditName(d.name);
    setEditDesc(d.description || '');
    setEditFunctionTypeId(d.referent_type_id);
    setEditActive(!!d.is_active);
  };

  const handleSaveEdit = async () => {
    if (!editing || editFunctionTypeId === '') return;
    setSaving(true);
    setError(null);
    try {
      await userService.updateReferentDefinition(editing.id, {
        name: editName.trim(),
        description: editDesc.trim(),
        referent_type_id: editFunctionTypeId,
        is_active: editActive,
      });
      setEditing(null);
      await load();
    } catch (e: any) {
      setError(e.message || 'Erreur à la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (d: ReferentFunctionDefinition) => {
    if (
      !window.confirm(
        `Désactiver la fonction « ${d.name} » ? Elle ne sera plus proposée aux nouvelles assignations.`
      )
    ) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await userService.deactivateReferentDefinition(d.id);
      await load();
    } catch (e: any) {
      setError(e.message || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return {
    types,
    definitions,
    loading,
    error,
    saving,
    newTypeKey,
    setNewTypeKey,
    newTypeName,
    setNewTypeName,
    newTypeDesc,
    setNewTypeDesc,
    newKey,
    setNewKey,
    newName,
    setNewName,
    newDesc,
    setNewDesc,
    newFunctionTypeId,
    setNewFunctionTypeId,
    editingType,
    setEditingType,
    editTypeKey,
    setEditTypeKey,
    editTypeName,
    setEditTypeName,
    editTypeDesc,
    setEditTypeDesc,
    editTypeActive,
    setEditTypeActive,
    editing,
    setEditing,
    editName,
    setEditName,
    editDesc,
    setEditDesc,
    editFunctionTypeId,
    setEditFunctionTypeId,
    editActive,
    setEditActive,
    handleCreateType,
    openEditType,
    handleSaveEditType,
    handleDeactivateType,
    handleCreate,
    openEdit,
    handleSaveEdit,
    handleDeactivate,
  };
}
